import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  C, PageHeader, GlassCard, Tabs, Badge, StatusDot, Button, Input, Skeleton,
  EmptyState, Modal, ProgressBar,
} from "@coremonit/design-system";
import {
  FolderKanban, ArrowLeft, Boxes, Users2, Layers, Settings2, LayoutDashboard,
  PlusCircle, Trash2, ShieldCheck, Server, Rocket, Activity, AlertTriangle,
  ShoppingCart, Package, CreditCard, Brain, PenSquare,
  type LucideIcon,
} from "lucide-react";
import {
  useProject, useProjectGroups, useProjectMembers, useProjectServices,
  useCreateProjectGroup, useDeleteProjectGroup,
  useAddProjectMember, useUpdateProjectMember, useRemoveProjectMember,
  useUpdateProject, useDeleteProject,
} from "../api/hooks";
import type {
  Environment, DeploymentType, ProjectGroup, ProjectGroupCreateInput,
  ProjectGroupKind, ProjectMember, ProjectRole, Service,
} from "@coremonit/types";

const ICON_MAP: Record<string, LucideIcon> = {
  ShoppingCart, Package, CreditCard, ShieldCheck, Brain, Server, Boxes, FolderKanban,
};

type TabId = "overview" | "services" | "groups" | "members" | "settings";

export function ProjectDetailPage() {
  const { id }  = useParams();
  const project = useProject(id);
  const nav     = useNavigate();
  const [tab, setTab] = useState<TabId>("overview");

  if (project.isLoading) {
    return <div style={{ display: "flex", flexDirection: "column", gap: 14 }}><Skeleton height={64} /><Skeleton height={220} /></div>;
  }
  if (project.isError || !project.data) {
    return (
      <GlassCard style={{ padding: 32 }}>
        <EmptyState icon={<FolderKanban size={22} />} title="Project not found" description="It may have been deleted or renamed." action={<Button variant="secondary" icon={<ArrowLeft size={12} />} onClick={() => nav("/projects")}>Back to projects</Button>} />
      </GlassCard>
    );
  }
  const p = project.data;
  const Icon = ICON_MAP[p.icon] ?? Boxes;

  const tabs = [
    { id: "overview", label: "Overview", icon: <LayoutDashboard size={12} /> },
    { id: "services", label: "Services", icon: <Boxes size={12} />,  count: p.serviceCount },
    { id: "groups",   label: "Groups",   icon: <Layers size={12} />, count: p.groupCount },
    { id: "members",  label: "Members",  icon: <Users2 size={12} />, count: p.memberCount },
    { id: "settings", label: "Settings", icon: <Settings2 size={12} /> },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader
        icon={
          <div
            style={{
              width: 24, height: 24, borderRadius: 8,
              background: `linear-gradient(135deg, ${p.color}, ${p.color}66)`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Icon size={13} color="#fff" />
          </div>
        }
        title={p.name}
        subtitle={`${p.key} · ${p.description}`}
        badge={
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <StatusDot status={p.status} pulse={p.status !== "healthy"} />
            <Badge color={p.status === "critical" ? "red" : p.status === "warning" ? "amber" : p.status === "unknown" ? "muted" : "green"}>{p.status}</Badge>
          </div>
        }
        actions={
          <>
            <Button variant="secondary" icon={<ArrowLeft size={12} />} onClick={() => nav("/projects")}>All projects</Button>
            <Button variant="secondary" icon={<Rocket size={12} />} onClick={() => nav(`/deployments?project=${p.id}`)}>Deployments</Button>
            <Button variant="glow" icon={<PlusCircle size={12} />} onClick={() => nav("/services/new")}>Add service</Button>
          </>
        }
      />

      <Tabs tabs={tabs} active={tab} onChange={(id) => setTab(id as TabId)} />

      {tab === "overview" && <OverviewTab p={p} />}
      {tab === "services" && <ServicesTab projectId={p.id} />}
      {tab === "groups"   && <GroupsTab   projectId={p.id} />}
      {tab === "members"  && <MembersTab  projectId={p.id} />}
      {tab === "settings" && <SettingsTab p={p} />}
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────
function OverviewTab({ p }: { p: NonNullable<ReturnType<typeof useProject>["data"]> }) {
  const services = useProjectServices(p.id);
  const groups   = useProjectGroups(p.id);
  const members  = useProjectMembers(p.id);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <Stat label="Availability"       value={`${p.metrics.availabilityPct.toFixed(2)}%`} icon={<Activity size={11} />}       color={C.green} />
        <Stat label="p95 latency"        value={`${p.metrics.latencyP95Ms} ms`}             icon={<Activity size={11} />}       color={C.blue}  />
        <Stat label="Error rate"         value={`${p.metrics.errorRatePct.toFixed(2)}%`}    icon={<AlertTriangle size={11} />}  color={p.metrics.errorRatePct > 1 ? C.red : C.green} />
        <Stat label="Requests / min"     value={p.metrics.requestsPerMin.toLocaleString()}  icon={<Rocket size={11} />}         color={C.cyan}  />
        <Stat label="Deploy success"     value={`${p.metrics.deploymentSuccessPct.toFixed(1)}%`} icon={<Rocket size={11} />}    color={C.blue}  />
        <Stat label="Open incidents"     value={p.incidentCount}                            icon={<AlertTriangle size={11} />}  color={p.incidentCount > 0 ? C.amber : C.green} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
        <GlassCard style={{ padding: 18 }}>
          <SectionTitle title="Services in this project" icon={<Boxes size={13} />} />
          {services.isLoading ? <Skeleton height={160} /> : services.data && services.data.items.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
              {services.data.items.slice(0, 8).map((s) => <ServiceRow key={s.id} s={s} />)}
              {services.data.items.length > 8 && (
                <div style={{ color: C.textMuted, fontSize: 10, textAlign: "center", padding: 6 }}>+{services.data.items.length - 8} more</div>
              )}
            </div>
          ) : (
            <EmptyState icon={<Boxes size={18} />} title="No services yet" description="Add services and assign them to this project to see rollups here." />
          )}
        </GlassCard>
        <GlassCard style={{ padding: 18 }}>
          <SectionTitle title="Groups" icon={<Layers size={13} />} />
          {groups.isLoading ? <Skeleton height={160} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
              {(groups.data?.items ?? []).slice(0, 6).map((g) => (
                <div key={g.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <span style={{ width: 6, height: 20, borderRadius: 2, background: g.color, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: C.text, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.name}</div>
                      <div style={{ color: C.textMuted, fontSize: 9 }}>{kindLabel(g.kind)}</div>
                    </div>
                  </div>
                  <Badge color="blue">{g.serviceCount}</Badge>
                </div>
              ))}
              {!groups.data?.items.length && <div style={{ color: C.textMuted, fontSize: 11, padding: 8 }}>No groups yet.</div>}
            </div>
          )}
        </GlassCard>
      </div>

      <GlassCard style={{ padding: 18 }}>
        <SectionTitle title="Members" icon={<Users2 size={13} />} />
        {members.isLoading ? <Skeleton height={80} /> : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            {(members.data?.items ?? []).map((m) => (
              <div key={m.userId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                <div style={{ width: 24, height: 24, borderRadius: 8, background: C.blueDim, color: C.blue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                  {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <div style={{ color: C.text, fontSize: 11, fontWeight: 600 }}>{m.name}</div>
                  <div style={{ color: C.textMuted, fontSize: 9 }}>{roleLabel(m.role)}</div>
                </div>
              </div>
            ))}
            {!members.data?.items.length && <div style={{ color: C.textMuted, fontSize: 11 }}>No members yet.</div>}
          </div>
        )}
      </GlassCard>
    </div>
  );
}

function ServiceRow({ s }: { s: Service }) {
  const nav = useNavigate();
  return (
    <button
      onClick={() => nav(`/services/${s.id}`)}
      style={{
        display: "grid", gridTemplateColumns: "20px 1.4fr 1fr 90px 80px 80px", gap: 8, alignItems: "center",
        padding: "8px 10px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10,
        cursor: "pointer", textAlign: "left",
      }}
    >
      <StatusDot status={s.status} />
      <div style={{ minWidth: 0 }}>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
        <div style={{ color: C.textMuted, fontSize: 10 }}>{s.team} · {s.language}</div>
      </div>
      <Badge color={s.environment === "production" ? "blue" : "muted"}>{s.environment}</Badge>
      <div style={{ color: s.latencyP95Ms > 500 ? C.amber : C.textSub, fontSize: 11, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{s.latencyP95Ms}ms</div>
      <div style={{ color: s.errorRatePct > 1 ? C.red : C.green, fontSize: 11, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{s.errorRatePct.toFixed(2)}%</div>
      <div style={{ color: s.replicas.available === s.replicas.desired ? C.green : C.amber, fontSize: 11, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{s.replicas.available}/{s.replicas.desired}</div>
    </button>
  );
}

// ─── Services tab ────────────────────────────────────────────────────────
function ServicesTab({ projectId }: { projectId: string }) {
  const groups   = useProjectGroups(projectId);
  const [groupId, setGroupId] = useState<string>("");
  const services = useProjectServices(projectId, groupId || undefined);
  const nav      = useNavigate();

  const items = services.data?.items ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <GlassCard style={{ padding: 10 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <FilterChip label="All" active={!groupId} onClick={() => setGroupId("")} count={items.length} color={C.blue} />
          {(groups.data?.items ?? []).map((g) => (
            <FilterChip key={g.id} label={g.name} active={groupId === g.id} onClick={() => setGroupId(g.id)} count={g.serviceCount} color={g.color} />
          ))}
        </div>
      </GlassCard>

      {services.isLoading ? (
        <Skeleton height={220} />
      ) : items.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((s) => <ServiceRow key={s.id} s={s} />)}
        </div>
      ) : (
        <GlassCard style={{ padding: 32 }}>
          <EmptyState
            icon={<Boxes size={22} />}
            title="No services in this scope"
            description="Add a service or adjust group filters."
            action={<Button variant="glow" icon={<PlusCircle size={12} />} onClick={() => nav("/services/new")}>Add service</Button>}
          />
        </GlassCard>
      )}
    </div>
  );
}

function FilterChip({ label, active, onClick, count, color }: { label: string; active: boolean; onClick: () => void; count?: number; color: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600,
        cursor: "pointer",
        color: active ? color : C.textSub,
        background: active ? `${color}22` : C.surfaceHigh,
        border: `1px solid ${active ? color + "88" : C.border}`,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 3, background: color }} />
      {label}
      {typeof count === "number" && <span style={{ padding: "0 5px", borderRadius: 4, background: active ? `${color}33` : C.surfaceMid, color: active ? color : C.textMuted, fontSize: 9, fontWeight: 700 }}>{count}</span>}
    </button>
  );
}

// ─── Groups tab ──────────────────────────────────────────────────────────
function GroupsTab({ projectId }: { projectId: string }) {
  const groups   = useProjectGroups(projectId);
  const services = useProjectServices(projectId);
  const remove   = useDeleteProjectGroup(projectId);
  const [creating, setCreating] = useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="glow" icon={<PlusCircle size={12} />} onClick={() => setCreating(true)}>New group</Button>
      </div>
      {groups.isLoading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
          {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={140} radius={14} />)}
        </div>
      ) : groups.data && groups.data.items.length ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
          {groups.data.items.map((g) => (
            <GlassCard key={g.id} style={{ padding: 14 }} accent={`${g.color}55`}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 6, height: 20, borderRadius: 2, background: g.color }} />
                  <div>
                    <div style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>{g.name}</div>
                    <div style={{ color: C.textMuted, fontSize: 10 }}>{kindLabel(g.kind)}</div>
                  </div>
                </div>
                <button
                  onClick={() => { if (confirm(`Delete group "${g.name}"? Services stay put.`)) remove.mutate(g.id); }}
                  style={{ background: "transparent", border: "none", color: C.textMuted, cursor: "pointer", padding: 4 }}
                  title="Delete group"
                ><Trash2 size={12} /></button>
              </div>
              {g.description && <div style={{ color: C.textSub, fontSize: 11, marginBottom: 8, lineHeight: 1.5 }}>{g.description}</div>}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                {g.targets.map((t) => <Badge key={t} color="muted">{t}</Badge>)}
              </div>
              <ProgressBar value={services.data ? Math.round((g.serviceCount / Math.max(1, services.data.items.length)) * 100) : 0} color={g.color} />
              <div style={{ marginTop: 6, color: C.textMuted, fontSize: 10, display: "flex", justifyContent: "space-between" }}>
                <span>{g.serviceCount} of {services.data?.items.length ?? 0} services</span>
              </div>
            </GlassCard>
          ))}
        </div>
      ) : (
        <GlassCard style={{ padding: 32 }}>
          <EmptyState
            icon={<Layers size={22} />}
            title="No groups yet"
            description="Groups let you carve a project by environment (production, staging), by cluster (EKS, AKS, GKE), or by region."
            action={<Button variant="glow" icon={<PlusCircle size={12} />} onClick={() => setCreating(true)}>New group</Button>}
          />
        </GlassCard>
      )}
      <CreateGroupModal projectId={projectId} open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

const ENV_TARGETS: Environment[]      = ["production", "staging", "development", "uat"];
const PROVIDER_TARGETS: DeploymentType[] = ["kubernetes", "docker", "ecs", "eks", "aks", "gke", "vm", "baremetal", "nomad", "openshift"];

function CreateGroupModal({ projectId, open, onClose }: { projectId: string; open: boolean; onClose: () => void }) {
  const create = useCreateProjectGroup(projectId);
  const services = useProjectServices(projectId);
  const clusters = useMemo(() => [...new Set((services.data?.items ?? []).map((s) => s.deployment.cluster).filter(Boolean) as string[])], [services.data]);
  const regions  = useMemo(() => [...new Set((services.data?.items ?? []).map((s) => s.deployment.region ).filter(Boolean) as string[])], [services.data]);

  const [form, setForm] = useState<ProjectGroupCreateInput>({ name: "", description: "", kind: "environment", targets: [], color: "#2D7FF9" });
  const [err, setErr] = useState<string | null>(null);

  const setKind = (kind: ProjectGroupKind) => setForm((f) => ({ ...f, kind, targets: [] }));
  const toggleTarget = (t: string) => setForm((f) => ({ ...f, targets: f.targets.includes(t) ? f.targets.filter((x) => x !== t) : [...f.targets, t] }));

  const targets =
    form.kind === "environment" ? ENV_TARGETS      :
    form.kind === "provider"    ? PROVIDER_TARGETS :
    form.kind === "cluster"     ? clusters         :
    form.kind === "region"      ? regions          :
    (services.data?.items ?? []).map((s) => s.id);

  const submit = async () => {
    setErr(null);
    if (!form.name.trim()) return setErr("Name is required");
    if (!form.targets.length && form.kind !== "custom") return setErr("Pick at least one target");
    try {
      await create.mutateAsync(form);
      onClose();
      setForm({ name: "", description: "", kind: "environment", targets: [], color: "#2D7FF9" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to create group");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New group"
      subtitle="Bundle services by environment, cluster, region, or provider."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="glow" icon={<PlusCircle size={12} />} loading={create.isPending} onClick={submit}>Create group</Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Name">
          <Input value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="e.g. Production EKS" />
        </Field>
        <Field label="Description">
          <Input value={form.description ?? ""} onChange={(v) => setForm((f) => ({ ...f, description: v }))} placeholder="Optional" />
        </Field>
        <Field label="Group by">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(["environment", "cluster", "provider", "region", "custom"] as const).map((k) => (
              <FilterChip key={k} label={kindLabel(k)} active={form.kind === k} onClick={() => setKind(k)} color={C.blue} />
            ))}
          </div>
        </Field>
        <Field label={`Targets (${form.targets.length} selected)`}>
          {targets.length ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {targets.map((t) => (
                <FilterChip key={t} label={t} active={form.targets.includes(t)} onClick={() => toggleTarget(t)} color={C.cyan} />
              ))}
            </div>
          ) : (
            <div style={{ color: C.textMuted, fontSize: 11, padding: 6 }}>No {form.kind} values available in this project.</div>
          )}
        </Field>
        {err && (
          <div style={{ padding: "8px 10px", borderRadius: 10, background: C.redDim, color: C.red, fontSize: 11, fontWeight: 600 }}>{err}</div>
        )}
      </div>
    </Modal>
  );
}

function kindLabel(k: ProjectGroupKind): string {
  return k === "environment" ? "By environment"
    : k === "cluster"        ? "By cluster"
    : k === "provider"       ? "By provider"
    : k === "region"         ? "By region"
    : "Custom";
}

// ─── Members tab ─────────────────────────────────────────────────────────
function MembersTab({ projectId }: { projectId: string }) {
  const members = useProjectMembers(projectId);
  const add     = useAddProjectMember(projectId);
  const update  = useUpdateProjectMember(projectId);
  const remove  = useRemoveProjectMember(projectId);
  const [email, setEmail] = useState("");
  const [role,  setRole]  = useState<ProjectRole>("viewer");
  const [err,   setErr]   = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (!email.includes("@")) return setErr("Invalid email");
    try { await add.mutateAsync({ email, role }); setEmail(""); }
    catch (e) { setErr(e instanceof Error ? e.message : "Failed to add member"); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <GlassCard style={{ padding: 14 }}>
        <SectionTitle title="Invite" icon={<Users2 size={13} />} />
        <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px", minWidth: 220 }}>
            <Input value={email} onChange={setEmail} placeholder="teammate@company.com" />
          </div>
          <RoleSelect value={role} onChange={setRole} />
          <Button variant="glow" icon={<PlusCircle size={12} />} loading={add.isPending} onClick={submit}>Invite</Button>
        </div>
        {err && (
          <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 10, background: C.redDim, color: C.red, fontSize: 11, fontWeight: 600 }}>{err}</div>
        )}
      </GlassCard>

      {members.isLoading ? <Skeleton height={200} /> : (
        <GlassCard style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
            <thead>
              <tr style={{ background: C.surfaceHigh, color: C.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>
                <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Added</Th><Th align="right"> </Th>
              </tr>
            </thead>
            <tbody>
              {(members.data?.items ?? []).map((m) => (
                <MemberRow
                  key={m.userId} m={m}
                  onChangeRole={(newRole) => update.mutate({ userId: m.userId, role: newRole })}
                  onRemove={() => { if (confirm(`Remove ${m.name} from this project?`)) remove.mutate(m.userId); }}
                />
              ))}
            </tbody>
          </table>
        </GlassCard>
      )}
    </div>
  );
}

function MemberRow({ m, onChangeRole, onRemove }: { m: ProjectMember; onChangeRole: (r: ProjectRole) => void; onRemove: () => void }) {
  const isAdmin = m.role === "project_admin";
  return (
    <tr style={{ borderTop: `1px solid ${C.border}` }}>
      <Td>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 26, height: 26, borderRadius: 8, background: C.blueDim, color: C.blue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
            {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </div>
          <span style={{ color: C.text, fontWeight: 600 }}>{m.name}</span>
        </div>
      </Td>
      <Td>{m.email}</Td>
      <Td>
        <RoleSelect value={m.role} onChange={onChangeRole} />
      </Td>
      <Td>{new Date(m.addedAt).toLocaleDateString()}</Td>
      <Td align="right">
        <button
          onClick={onRemove}
          disabled={isAdmin}
          title={isAdmin ? "Owners can't be removed here" : "Remove member"}
          style={{ background: "transparent", border: "none", color: isAdmin ? C.textMuted : C.red, cursor: isAdmin ? "not-allowed" : "pointer", opacity: isAdmin ? 0.5 : 1, padding: 4 }}
        >
          <Trash2 size={13} />
        </button>
      </Td>
    </tr>
  );
}

function RoleSelect({ value, onChange }: { value: ProjectRole; onChange: (r: ProjectRole) => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, padding: "5px 8px" }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as ProjectRole)}
        style={{ background: "transparent", border: "none", color: C.text, fontSize: 11, outline: "none", cursor: "pointer" }}
      >
        <option value="project_admin" style={{ background: C.surface }}>Project admin</option>
        <option value="editor"        style={{ background: C.surface }}>Editor</option>
        <option value="viewer"        style={{ background: C.surface }}>Viewer</option>
      </select>
    </div>
  );
}

function roleLabel(r: ProjectRole): string {
  return r === "project_admin" ? "Project admin" : r === "editor" ? "Editor" : "Viewer";
}

// ─── Settings tab ────────────────────────────────────────────────────────
function SettingsTab({ p }: { p: NonNullable<ReturnType<typeof useProject>["data"]> }) {
  const update = useUpdateProject(p.id);
  const del    = useDeleteProject();
  const nav    = useNavigate();
  const [name,  setName]  = useState(p.name);
  const [desc,  setDesc]  = useState(p.description);
  const [owner, setOwner] = useState(p.ownerEmail);

  const save = async () => { await update.mutateAsync({ name, description: desc, ownerEmail: owner }); };
  const destroy = async () => {
    if (!confirm(`Delete project "${p.name}"? Services stay, but assignments are cleared.`)) return;
    await del.mutateAsync(p.id);
    nav("/projects");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <GlassCard style={{ padding: 18 }}>
        <SectionTitle title="General" icon={<PenSquare size={13} />} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
          <Field label="Name"><Input value={name} onChange={setName} /></Field>
          <Field label="Owner email"><Input value={owner} onChange={setOwner} /></Field>
          <div style={{ gridColumn: "1 / -1" }}>
            <Field label="Description"><Input value={desc} onChange={setDesc} /></Field>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <Button variant="primary" loading={update.isPending} onClick={save}>Save changes</Button>
        </div>
      </GlassCard>

      <GlassCard style={{ padding: 18 }} accent={`${C.red}55`}>
        <SectionTitle title="Danger zone" icon={<AlertTriangle size={13} />} />
        <div style={{ color: C.textSub, fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
          Deleting this project un-assigns its services and removes its groups and member list. Services and their telemetry are untouched.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <Button variant="danger" icon={<Trash2 size={12} />} loading={del.isPending} onClick={destroy}>Delete project</Button>
        </div>
      </GlassCard>
    </div>
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────────
function SectionTitle({ title, icon }: { title: string; icon?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: C.blue, display: "inline-flex" }}>{icon}</span>
      <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</div>
    </div>
  );
}
function Stat({ label, value, color, icon }: { label: string; value: React.ReactNode; color: string; icon?: React.ReactNode }) {
  return (
    <GlassCard style={{ padding: 12 }}>
      <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
        {icon}{label}
      </div>
      <div style={{ color, fontSize: 20, fontWeight: 700, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </GlassCard>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ color: C.textSub, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      {children}
    </div>
  );
}
const Th = ({ children, align }: { children: React.ReactNode; align?: "left" | "right" }) =>
  <th style={{ padding: "10px 14px", textAlign: align ?? "left", fontWeight: 700 }}>{children}</th>;
const Td = ({ children, align }: { children: React.ReactNode; align?: "left" | "right" }) =>
  <td style={{ padding: "10px 14px", textAlign: align ?? "left", color: C.textSub }}>{children}</td>;
